import { useState } from 'react';

import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  DataTable,
  SingleSelectDropdown,
} from '@repo/videa-components/src/index';
import { Input } from '@repo/videa-components/src/ui/input';
import type { ColumnFiltersState, SortingState } from '@tanstack/react-table';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { PrivacyButton } from '@/modules/patientList/PrivacyButton';
import type { TProviderTreatment } from '@/react-query/insights/types';

import { useClinicalProviderTableColumns } from './ClinicalProviderTableColumns';

export const ClinicalProviderTable = ({
  treatments,
  isLoading,
}: {
  treatments?: TProviderTreatment[];
  isLoading?: boolean;
}) => {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  // new pins to top, then curated, then by date
  const [sorting, setSorting] = useState<SortingState>([
    {
      id: 'recommendationPinId',
      desc: false,
    },
    {
      id: 'isCurated',
      desc: false,
    },
    {
      id: 'apptDate',
      desc: true,
    },
  ]);

  // new pins to top
  const [selectedFilter, setSelectedFilter] = useState<string>(
    'All Recommendations',
  );
  const filters = [
    'All Recommendations',
    'Pinned',
    'Unplanned',
    'Planned',
    'Scheduled',
    'Completed',
    'Secondary Treatments',
  ];

  if (treatments?.some((treatment) => treatment.isCurated)) {
    filters.unshift('Curated');
  }

  const table = useReactTable({
    data: treatments ?? [],
    columns: useClinicalProviderTableColumns({
      isCurated: treatments?.some((treatment) => treatment.isCurated),
    }),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    enableRowSelection: true,
    state: {
      sorting,
      columnFilters,
    },
  });
  return (
    <div className="vai-flex vai-flex-col vai-gap-4 vai-w-full">
      <div className="vai-flex vai-flex-row vai-justify-between">
        <span className="vai-typography-large">AI Recommendations</span>
        <div className="vai-flex vai-flex-row vai-items-center vai-gap-2">
          <div className="vai-relative vai-mx-2">
            <FontAwesomeIcon
              icon={faMagnifyingGlass}
              className="vai-absolute vai-left-3 vai-top-1/2 vai-transform vai--translate-y-1/2 vai-text-gray-400 vai-z-10"
            />
            <Input
              placeholder="Search patients"
              value={
                (table
                  .getColumn('pmsPatientDisplayName')
                  ?.getFilterValue() as string) ?? ''
              }
              onChange={(event) =>
                table
                  .getColumn('pmsPatientDisplayName')
                  ?.setFilterValue(event.target.value)
              }
              className="vai-w-full vai-pl-10 vai-pr-8"
            />
          </div>
          <SingleSelectDropdown
            items={filters}
            selectedItem={selectedFilter}
            onChange={(selected) => {
              setSelectedFilter(selected);
              if (selected === 'All Recommendations') {
                table.getColumn('status')?.setFilterValue(undefined);
                table.getColumn('correlation')?.setFilterValue(undefined);
                table
                  .getColumn('recommendationPinId')
                  ?.setFilterValue(undefined);
                table.getColumn('isCurated')?.setFilterValue(undefined);
              } else if (selected === 'Curated') {
                table.getColumn('isCurated')?.setFilterValue(true);
                table.getColumn('status')?.setFilterValue(undefined);
                table.getColumn('correlation')?.setFilterValue(undefined);
                table
                  .getColumn('recommendationPinId')
                  ?.setFilterValue(undefined);
              } else if (selected === 'Pinned') {
                table.getColumn('recommendationPinId')?.setFilterValue(true);
                table.getColumn('correlation')?.setFilterValue(undefined);
                table.getColumn('status')?.setFilterValue(undefined);
                table.getColumn('isCurated')?.setFilterValue(undefined);
              } else if (selected === 'Secondary Treatments') {
                table.getColumn('status')?.setFilterValue(undefined);
                table.getColumn('correlation')?.setFilterValue(selected);
                table
                  .getColumn('recommendationPinId')
                  ?.setFilterValue(undefined);
                table.getColumn('isCurated')?.setFilterValue(undefined);
              } else {
                table.getColumn('status')?.setFilterValue(selected);
                table.getColumn('correlation')?.setFilterValue(undefined);
                table
                  .getColumn('recommendationPinId')
                  ?.setFilterValue(undefined);
                table.getColumn('isCurated')?.setFilterValue(undefined);
              }
            }}
            getItemLabel={(item) => item}
            getItemValue={(item) => item}
          />
          <PrivacyButton />
        </div>
      </div>
      <DataTable
        table={table}
        isLoading={isLoading}
        className="vai-h-[calc(100vh-400px)]"
      />
    </div>
  );
};
